import makeStyles from '@mui/styles/makeStyles';
import {getSubTitle, groupCompatibilityStatus, statuses, trackerStatus} from "business/matching";
import classNames from "classnames";
import Avatar from "components/Matching/Avatar";
import ExpiryDurationMessage from "components/Matching/ExpiryDurationMessage";
import InfoPill from "components/Matching/InfoPill";
import MatchingCriteriaGroup from "components/Matching/MatchingCriteriaGroup";
import StatusBadge from "components/Matching/StatusBadge";
import MatchingInfoModal from "components/MatchingInfoModal";
import {Ban, Check, ChevronRight, CircleCheck, Clock, Info, Users, X} from "lucide-react";
import {get, isEmpty, noop} from "lodash";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {color, font, match as matchTokens, radius, shadow, type} from "common/tokens";

// Sizes follow the Figma frames: 12px card padding, 36px avatar, a 25px-high
// action pill, and a full-bleed needs strip tinted against the card.
const useStyles = makeStyles(theme => ({
  card: {
    background: color.SURFACE,
    borderRadius: radius.MEDIA_CARD,
    // Held at the selected width so picking a card recolours it without
    // resizing it — a thicker border on selection nudges the whole list.
    border: `1px solid ${color.CARD_BORDER}`,
    boxShadow: shadow.CARD,
    padding: 16,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  clickable: {
    cursor: 'pointer',
  },
  selected: {
    borderColor: matchTokens.CARD_SELECTED_BORDER,
    background: matchTokens.CARD_SELECTED_BG,
  },
  muted: {
    background: matchTokens.CARD_MUTED_BG,
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    ...type.sessionTitle,
    color: color.TEXT,
  },
  nameMuted: {
    color: color.TEXT_MUTED,
  },
  subTitle: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    marginTop: 4,
  },
  // Held open even when empty: pair-complete lands on the other cards the
  // moment one is picked, and a badge appearing would grow the card and shove
  // the list around mid-decision. Sits outside the name column so the avatar
  // centres against the name and subtitle alone.
  badgeSlot: {
    minHeight: 18,
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
  },
  chevron: {
    color: color.TEXT_MUTED,
    display: 'flex',
    flexShrink: 0,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginTop: 14,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 10,
    },
  },
  actions: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
  },
  action: {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 25,
    padding: '0 12px',
    borderRadius: radius.PILL,
    border: `1px solid ${color.BORDER}`,
    background: color.SURFACE,
    cursor: 'pointer',
    color: color.TEXT_MUTED,
    whiteSpace: 'nowrap',
    fontFamily: font.SANS,
    fontSize: 11,
    fontWeight: font.BOLD,
  },
  accepted: {
    background: matchTokens.ACCEPT_BG,
    borderColor: matchTokens.ACCEPT_BG,
    color: matchTokens.ACCEPT_FG,
  },
  rejected: {
    background: matchTokens.REJECT_BG,
    borderColor: matchTokens.REJECT_BG,
    color: matchTokens.ACCEPT_FG,
  },
  actionDisabled: {
    borderColor: matchTokens.CARD_BORDER,
    background: matchTokens.CARD_MUTED_BG,
    cursor: 'default',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 10,
    paddingTop: 10,
    borderTop: `1px solid ${matchTokens.CARD_BORDER}`,
    ...type.meta,
    fontWeight: font.MEDIUM,
  },
  // Full-bleed against the card's 12px padding, as in the frames.
  criteria: {
    margin: '16px -16px -16px',
    padding: '14px 16px',
    background: matchTokens.CHIP_BG,
  },
}));

const ActionButton = ({label, icon: Icon, selected, disabled, selectedClass, onClick}) => {
  const classes = useStyles();
  const filled = selected && !disabled;

  return (
    <button
      type={'button'}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={classNames(classes.action, filled && selectedClass, disabled && classes.actionDisabled)}
    >
      <Icon size={12} strokeWidth={2.75}/>
      {label}
    </button>
  );
};

const MatchSummary = ({
  match,
  expiryHours,
  onUpdateMatch = noop,
  infoOnly = false,
  allowReject = true,
  showExpiryDuration = true,
  showStatus = false,
  showUserInfo = true,
  showCriteria = false,
  action = 'info',
  avatarSize = 36,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [open, setOpen] = useState(false);

  const status = get(match, 'status');
  const groupCompatibility = get(match, 'groupCompatibility');
  const displayName = get(match, 'displayName');

  const isActive = status === statuses.ACTIVE;
  const isExpired = status === statuses.EXPIRED;
  const isSuccess = ['SUCCESS', 'MENTOR_INTERESTED_GROUP_HOLD', 'MENTOR_INTERESTED_GROUP'].includes(status);
  const isSelected = get(match, 'interestStatus') === trackerStatus.INTERESTED;
  const isRejected = get(match, 'interestStatus') === trackerStatus.REJECTED;
  const isIncompatible = groupCompatibility === groupCompatibilityStatus.INCOMPATIBLE;
  const groupFull = groupCompatibility === groupCompatibilityStatus.GROUP_FULL;
  const isPairComplete = groupCompatibility === groupCompatibilityStatus.PAIR_COMPLETE;

  const blocked = isIncompatible || groupFull || isPairComplete;
  const muted = blocked || isExpired;

  const statusMessage = isEmpty(displayName) ? null : t(displayName, {keySeparator: '>', nsSeparator: '|'});
  const statusTone = isActive || isSuccess ? matchTokens.STATUS_OPEN : matchTokens.EXPIRY_URGENT;

  const badge = () => {
    if (groupFull) return {label: t('groupFull'), tone: 'neutral', icon: Users};
    if (isIncompatible) return {label: t('incompatible'), tone: 'danger', icon: Ban};
    if (isPairComplete) return {label: t('pairComplete'), tone: 'neutral', icon: CircleCheck};
    if (isExpired) return {label: t('matchExpiredBadge'), tone: 'danger', icon: Clock};
    return null;
  };

  const currentBadge = badge();
  // Only a card that still has buttons can gain a badge while being looked at;
  // history cards are static, so they need no reserved space.
  const canBadgeLater = isActive && !infoOnly;
  const showActions = isActive && !infoOnly;
  const showExpiry = showExpiryDuration && isActive;

  const openInfo = () => setOpen(true);
  const cardOpens = action === 'chevron';
  const keyActivate = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openInfo();
    }
  };

  return (
    <>
    <div
      role={cardOpens ? 'button' : undefined}
      tabIndex={cardOpens ? 0 : undefined}
      onClick={cardOpens ? openInfo : undefined}
      onKeyDown={cardOpens ? keyActivate : undefined}
      aria-label={cardOpens ? `${get(match, 'fullName')}, ${t('moreInfo')}` : undefined}
      className={classNames(classes.card, cardOpens && classes.clickable, isSelected && classes.selected, blocked && classes.muted)}>
      <div className={classes.identity}>
        <Avatar displayName={get(match, 'fullName', '')} displayPicture={get(match, 'displayPicture')}
                isSelected={isSelected} size={avatarSize} fontSize={avatarSize <= 30 ? 11 : 13}/>
        <div className={classes.text}>
          <div className={classNames(classes.name, muted && classes.nameMuted)}>{get(match, 'fullName')}</div>
          {showUserInfo && <div className={classes.subTitle}>{getSubTitle(match)}</div>}
        </div>
        {cardOpens
          ? <span className={classes.chevron}><ChevronRight size={16}/></span>
          : <InfoPill onClick={openInfo} label={`${get(match, 'fullName')}, ${t('moreInfo')}`}/>}
      </div>

      {(currentBadge || canBadgeLater) && (
        <div className={classes.badgeSlot} style={{marginLeft: avatarSize + 12}}>
          {currentBadge && <StatusBadge {...currentBadge}/>}
        </div>
      )}

      {(showExpiry || showActions) && (
        <div className={classes.metaRow}>
          {showExpiry
            ? <ExpiryDurationMessage timeStamp={get(match, 'updatedTimestamp')} expiryHours={expiryHours}/>
            : <span/>}
          {showActions && (
            <div className={classes.actions} onClick={event => event.stopPropagation()}>
              {allowReject && (
                <ActionButton label={t('notInterested')} icon={X} selected={isRejected} disabled={blocked}
                              selectedClass={classes.rejected}
                              onClick={() => onUpdateMatch(trackerStatus.REJECTED)}/>
              )}
              <ActionButton label={t('interested')} icon={Check} selected={isSelected} disabled={blocked}
                            selectedClass={classes.accepted}
                            onClick={() => onUpdateMatch(trackerStatus.INTERESTED)}/>
            </div>
          )}
        </div>
      )}

      {showStatus && Boolean(statusMessage) && (
        <div className={classes.statusRow} style={{color: statusTone}}>
          <Info size={12} strokeWidth={2.25} style={{flexShrink: 0, marginTop: 2}}/>
          {statusMessage}
        </div>
      )}

      {showCriteria && (
        <div className={classes.criteria}>
          <MatchingCriteriaGroup criterias={get(match, 'exclusiveCriteria')}
                                 clashingCriterias={get(match, 'clashingCriterias', [])}
                                 onTint/>
        </div>
      )}

    </div>

    {/* A sibling of the card, never a child: the card opens on click, and React
        bubbles events through the tree rather than the DOM, so a modal nested
        inside it would reopen itself on the click that closed it.
        Mounted on open so a list of cards does not fetch every profile up front. */}
    {open && <MatchingInfoModal open handleClose={() => setOpen(false)} matchId={get(match, 'id')}
                                fullName={get(match, 'fullName')} statusMessage={statusMessage}/>}
    </>
  );
};

export default MatchSummary;
